import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ExternalLink, Wrench, Briefcase, ClipboardList } from "lucide-react";

const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const isTecnica = (c) => ((c.tool || c.summary?.tool || "").toLowerCase() === "tetto");

export default function ConsulenzeAnagrafica({ installerId, testid = "consulenze-anagrafica" }) {
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);

  useEffect(() => {
    if (!installerId) return;
    setLoading(true);
    api.get(`/rete/configs?installer_id=${installerId}`)
      .then((r) => {
        const list = (r.data?.configs || [])
          .filter((c) => c.tool || c.summary?.cliente || c.summary?.n_pannelli != null)
          .slice().sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        setConfigs(list);
      })
      .catch(() => setConfigs([]))
      .finally(() => setLoading(false));
  }, [installerId]);

  return (
    <Card className="p-6" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-lg font-bold tracking-tight">Consulenze dal configuratore</h2>
        {!loading && <Badge variant="secondary" className="ml-1" data-testid={`${testid}-count`}>{configs.length}</Badge>}
      </div>
      {loading ? (
        <div className="py-8 text-center text-muted-foreground" data-testid={`${testid}-loading`}><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
      ) : configs.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground" data-testid={`${testid}-empty`}>Nessuna consulenza per questa anagrafica.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente finale</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Pannelli</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Apri</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((c) => {
                const s = c.summary || {};
                const tech = isTecnica(c);
                const pann = s.n_pannelli != null
                  ? `${s.n_pannelli} pann.${s.potenza_kwp ? ` · ${s.potenza_kwp} kWp` : ""}`
                  : "—";
                return (
                  <TableRow key={c.id} data-testid={`${testid}-row-${c.id}`}>
                    <TableCell className="font-semibold">{s.cliente || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{itDate(c.created_at)}</TableCell>
                    <TableCell className="text-sm">{pann}</TableCell>
                    <TableCell>
                      {tech
                        ? <Badge className="gap-1 border-blue-300 bg-blue-100 text-blue-800" data-testid={`${testid}-tipo-${c.id}`}><Wrench className="h-3 w-3" /> Tecnica</Badge>
                        : <Badge className="gap-1 border-amber-300 bg-amber-100 text-amber-800" data-testid={`${testid}-tipo-${c.id}`}><Briefcase className="h-3 w-3" /> Commerciale</Badge>}
                      {c.tool_label ? <span className="ml-2 text-[11px] text-muted-foreground">{c.tool_label}</span> : null}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.consulenza_url
                        ? <a href={c.consulenza_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid={`${testid}-open-${c.id}`}>Apri <ExternalLink className="h-3.5 w-3.5" /></a>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
